<template>
  <div class="map-container">
    <div id="map" style="width: 100%; height: 70vh;"></div>
    <button class="fullscreen-btn" @click="toggleFullScreen">
      ⛶ Full Screen
    </button>
  </div>
</template>

<script>
import DashboardDataService from "../services/DashboardDataService";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const truckIcon = L.divIcon({
  html: `<i class="fas fa-truck" style="font-size:24px;"></i>`,
  className: "custom-fa-icon",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

export default {
  name: 'FleetMap',
  props: {
    devicesLocation: { type: Array, required: true }
  },
  data() {
    return {
      map: null,
      markers: [],       // << Add this
      polylines: [],
      animatedMarker: null,
      currentRouteId: null, // track currently shown route's device serial number
    };
  },
  mounted() {
    this.initMap();
  },
  methods: {
    async fetchDeviceRoute(serialNumber) {
      try {
        const data = { serialNumber };
        const response = await DashboardDataService.fetchDeviceRoute(data);
        return response.data.data;
      } catch (e) {
        this.$toast.error(e.message || "Failed to fetch route");
        return [];
      }
    },

    initMap() {
      this.map = L.map('map').setView([20.5937, 78.9629], 5);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map);

      this.refreshMarkers(this.devicesLocation);
    },

    loadMarkers() {
      const bounds = [];

      this.devicesLocation.forEach(device => {
        const { name, lastLocation } = device;
        const { lat, long } = lastLocation;

        const marker = L.marker([lat, long], { icon: truckIcon }).addTo(this.map);

        marker.bindTooltip(name, { 
          permanent: true, 
          direction: 'top', 
          offset: [0, -30]
        });

        marker.on('click', async () => {
          // If this device's route is already shown, do nothing
          if (this.currentRouteId === device.serialNumber) {
            return;
          }

          // Clear any previous route and animated marker
          this.clearCurrentRoute();

          const route = await this.fetchDeviceRoute(device.serialNumber);
          this.currentRouteId = device.serialNumber;
          this.showRoute(route);
        });

        bounds.push([lat, long]);
      });

      this.map.fitBounds(bounds, { padding: [50, 50] });
    },

    clearCurrentRoute() {
      this.polylines.forEach(polyline => this.map.removeLayer(polyline));
      this.polylines = [];

      if (this.animatedMarker) {
        this.map.removeLayer(this.animatedMarker);
        this.animatedMarker = null;
      }

      this.currentRouteId = null;
    },

    showRoute(route) {
      const latLngs = route.map(p => [p.lat, p.long]);
      const polyline = L.polyline(latLngs, { weight: 4 }).addTo(this.map);
      this.polylines.push(polyline);
      this.map.fitBounds(polyline.getBounds());

      this.animatedMarker = L.marker(latLngs[0], { icon: truckIcon }).addTo(this.map);

      let currentIndex = 0;
      this.animatedMarker.bindPopup('', {
        offset: L.point(0, -30),
        className: 'custom-time-popup'
      }).openPopup();

      const animate = () => {
        if (currentIndex < latLngs.length - 1) {
          const start = latLngs[currentIndex];
          const end = latLngs[currentIndex + 1];
          const timeInfo = route[currentIndex].time || 'N/A';

          let progress = 0;
          const steps = 60;
          const interval = setInterval(() => {
            progress++;
            const lat = start[0] + ((end[0] - start[0]) * progress) / steps;
            const lng = start[1] + ((end[1] - start[1]) * progress) / steps;
            this.animatedMarker.setLatLng([lat, lng]);

            this.animatedMarker.getPopup().setContent(`<strong>Time:</strong> ${timeInfo}`);
            if (!this.map.hasLayer(this.animatedMarker.getPopup())) {
              this.animatedMarker.openPopup();
            }

            if (progress === steps) {
              clearInterval(interval);
              currentIndex++;
              animate();
            }
          }, 100);
        }
      };

      animate();
    },

    toggleFullScreen() {
      const mapEl = document.getElementById('map');
      if (!document.fullscreenElement) {
        mapEl.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    },

    refreshMarkers(newDevices) {
      // Remove old markers
      this.markers.forEach(m => this.map.removeLayer(m));
      this.markers = [];

      // Remove old routes
      this.clearCurrentRoute();

      if (!newDevices.length) return;

      const bounds = [];

      newDevices.forEach(device => {
        const { name, lastLocation } = device;
        const { lat, long } = lastLocation;

        const marker = L.marker([lat, long], { icon: truckIcon }).addTo(this.map);

        marker.bindTooltip(name, {
          permanent: true,
          direction: "top",
          offset: [0, -30]
        });

        // Same click logic as before
        marker.on("click", async () => {
          if (this.currentRouteId === device.serialNumber) return;

          this.clearCurrentRoute();

          const route = await this.fetchDeviceRoute(device.serialNumber);
          this.currentRouteId = device.serialNumber;
          this.showRoute(route);
        });

        this.markers.push(marker);
        bounds.push([lat, long]);
      });

      this.map.fitBounds(bounds, { padding: [40, 40] });
    }

  },
  watch: {
    devicesLocation: {
      deep: false,
      handler(newVal) {
        this.refreshMarkers(newVal);
      }
    }
  }

};
</script>

<style scoped>
.leaflet-tooltip {
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.step-marker {
  background: #007bff;
  color: white;
  width: 20px;
  height: 20px;
  text-align: center;
  line-height: 20px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
}

.map-container {
  position: relative;
  width: 100%;
  height: 100vh;
}

.fullscreen-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  border: 1px solid #ccc;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  z-index: 1000;
}

.fullscreen-btn:hover {
  background: #f1f1f1;
}

.leaflet-popup-content-wrapper {
  background-color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 6px !important;
  box-shadow: 0 0 8px rgba(0,0,0,0.15) !important;
  padding: 6px 12px !important;
  color: #333 !important;
  font-weight: 600;
  font-size: 14px;
}

.leaflet-popup-tip {
  background-color: rgba(255, 255, 255, 0.9) !important;
}
</style>
